import React, { useEffect, useMemo, useState } from "react"
import {
	Autocomplete, Box, Chip, Stack, TextField, Typography,
} from "@mui/material"
import { ArrowRightAlt as ArrowIcon } from 'Pages/DsRepricing/@components/icons'
import { IconSeeker } from "@ui-seeker/core"
import { dsColorNew } from "@palette"
import { AppNote } from "@components"

import { dsRepricingDrawerData } from "Pages/DsRepricing/Page/@signals"
import { useDraftRateShops } from "Pages/DsRepricing/Page/@hooks/useRateShops"
import { setupApi } from "Pages/DsRepricing/Setup/@services"
import { useCategories } from "Pages/DsRepricing/Setup/@services/useCategories"
import { usePricelistCatalog } from "Pages/DsRepricing/Setup/@services/pricelists"
import { useProductsCatalog } from "Pages/DsRepricing/Setup/@services/useProducts"
import { tenantBrackets, valueLabel, fitSentence } from "Pages/DsRepricing/Setup/@services/tethers"

/**
 * Tethers · per-strategy selection.
 *
 * The tethering rules themselves live in Setup → Tethering; each
 * strategy just picks WHICH of those existing rules should apply to
 * its own repricings. The list here is a subset of setup rules; when
 * a rule is deleted from Setup the reference here is dropped
 * automatically on next open — only when the library did load: a load
 * error keeps every chosen id (CU-869fkr2yp, criterion 21).
 *
 * Each tether says whether it applies to this strategy, partly or not,
 * by the engine's rule (`tethers[id].fits` of the rate-shops estimate the
 * drawer already asks for, criteria 9-11). Nothing blocks the save.
 */

const DIM_PALETTE = {
	lor:      { bg: dsColorNew.pillActiveBg, fg: dsColorNew.appBlue, border: dsColorNew.noteInfoBorder, mono: false },
	category: { bg: dsColorNew.noteInfoBg, fg: dsColorNew.noteInfo, border: dsColorNew.noteInfoBorder, mono: false },
	acriss:   { bg: dsColorNew.noteAmberBg, fg: dsColorNew.noteAmber, border: dsColorNew.noteAmberBorder, mono: true  },
	office:   { bg: dsColorNew.successBg, fg: dsColorNew.successText, border: dsColorNew.successBar, mono: true  },
}
const DIM_LABEL = { lor: 'LOR', category: 'Category', acriss: 'ACRISS', office: 'Office' }
const NOT_APPLIED = 'Not applied by the engine yet'
const NO_PRODUCT = 'Pick a product in Basic, card 2, to check LOR tethers'

/**
 * The chip label of a tether value: the bracket label for LOR (the same
 * one as Setup and the wizard's Rental lengths, criterion 22), the
 * tenant's class name for category, the code otherwise.
 */
function useValueLabel() {
	const { tree } = useCategories()
	const { items: pricelists } = usePricelistCatalog()
	const { items: products } = useProductsCatalog()
	return useMemo(() => {
		const ctx = { brackets: tenantBrackets(products, pricelists), categories: tree }
		return (dim, code) => valueLabel(dim, code, ctx)
	}, [tree, pricelists, products])
}

function formatDelta(d) {
	if (!d || d.v == null) return ''
	const sign = d.v > 0 ? '+' : ''
	return d.k === 'pct' ? `${sign}${d.v}%` : `${sign}${d.v}€`
}

/**
 * The tether library of the tenant, from Setup → Tethering
 * (`/api/repricing/setup/tethers`). The strategy only ever stores ids;
 * the definitions belong to Setup, which is why there is no inline
 * creation here (PRD 02 §3).
 *
 * Read once when the step opens. A tether created in another tab appears
 * on the next open, which is the same guarantee the rest of the wizard
 * gives for offices and pricelists. A failed read is an error, never an
 * empty library (criterion 21).
 */
function useTetherLibrary() {
	const [state, setState] = useState({ library: [], loading: true, error: null })

	useEffect(() => {
		let alive = true
		setupApi.tethers.list()
			.then(rows => alive && setState({ loading: false, error: null, library: rows.map(t => ({
				id: t.id,
				name: t.name,
				dimension: t.dimension,
				parent: t.parent_value,
				children: (t.children || []).map(c => c.child_value ?? c),
				delta: { k: t.delta_kind, v: Number(t.delta_value) },
			})) }))
			.catch(e => alive && setState({ library: [], loading: false, error: e || true }))
		return () => { alive = false }
	}, [])

	return state
}

/**
 * The engine's fit of every tether for this draft, as sentences:
 * `{ checked, check(tether) → { sentence, applies, misfits } }`.
 * Category never applies; without a product a LOR tether asks for one;
 * without an estimate nothing is invented (`checked` false).
 */
function useTetherChecks(data) {
	const shop = useDraftRateShops()
	const label = useValueLabel()
	const { items: products } = useProductsCatalog()
	const product = data.productId == null ? null
		: (products || []).find(p => String(p.id) === String(data.productId)) || null
	const entry = shop.data?.tethers?.[String(data.id ?? 'draft')] || null
	const fits = Array.isArray(entry?.fits) ? entry.fits : null
	const check = (t) => {
		if (t.dimension === 'category') return { sentence: NOT_APPLIED, applies: false, misfits: [] }
		if (data.productId == null && t.dimension === 'lor') return { sentence: NO_PRODUCT, applies: false, misfits: [] }
		const fit = fits?.find(f => String(f.tether_id) === String(t.id))
		if (!fit) return null
		const sentence = fitSentence(fit, { productName: product?.name, value: (v) => label(t.dimension, v) })
		const misfits = (fit.children || []).filter(c => !c.fits).map(c => String(c.value))
		return { sentence, applies: !!fit.applies, misfits }
	}
	return { checked: !!fits, loading: !!shop.loading, product, check }
}

export default function DsRepricingTethers() {
	const data = dsRepricingDrawerData.value
	const { library, loading, error } = useTetherLibrary()
	const checks = useTetherChecks(data)
	const selectedIds = Array.isArray(data.tethers) ? data.tethers : []

	// Drop references to tethers that no longer exist in the library
	// (someone deleted them from Setup) — only from a library that loaded:
	// after a failed read every chosen id is kept.
	useEffect(() => {
		if (loading || error) return
		if (!selectedIds.length || !library.length) return
		const liveIds = new Set(library.map(t => t.id))
		const cleaned = selectedIds.filter(id => liveIds.has(id))
		if (cleaned.length !== selectedIds.length) {
			dsRepricingDrawerData.value = { ...dsRepricingDrawerData.peek(), tethers: cleaned }
		}
	}, [library, loading, error])   // eslint-disable-line react-hooks/exhaustive-deps

	function onChange(next) {
		dsRepricingDrawerData.value = {
			...dsRepricingDrawerData.peek(),
			tethers: next.map(t => t.id),
		}
	}

	if (error) {
		return (
			<Stack gap={1}>
				<AppNote severity="warning">
					Could not load the tethers — the ones already chosen are kept.
				</AppNote>
				{selectedIds.length > 0 && (
					<Stack direction="row" gap={0.5} flexWrap="wrap">
						{selectedIds.map(id => (
							<Chip key={id} size="small" label={`Tether #${id}`}
							      sx={{ height: 22, fontSize: 11, fontWeight: 700 }}/>
						))}
					</Stack>
				)}
			</Stack>
		)
	}

	if (!loading && library.length === 0) {
		return (
			<Box sx={{ p: 1.25, bgcolor: dsColorNew.tableRowAlt,
			           border: `1px dashed ${dsColorNew.grayBorder}`,
			           borderRadius: '10px', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
				<IconSeeker icon="circle-info"
				            style={{ color: dsColorNew.textMuted, marginTop: 3, fontSize: 16 }}/>
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted,
				                  lineHeight: 1.5 }}>
					No tethers defined yet. Create them in{' '}
					<strong>Setup → Tethering</strong> to make them selectable here.
				</Typography>
			</Box>
		)
	}

	const selected = library.filter(t => selectedIds.includes(t.id))

	return (
		<Stack gap={1}>
			<Typography sx={{ fontSize: 12.5, color: dsColorNew.textMuted, lineHeight: 1.5 }}>
				{checks.checked || data.productId == null
				  ? <>Checked against this strategy: product{checks.product?.name ? <> <strong>{checks.product.name}</strong></> : ''} · Rental lengths · Offices · Cars. Change them in steps 1–3 and the checks follow.</>
				  : checks.loading
				    ? 'Checking the tethers against this strategy…'
				    : 'Checked once the strategy can be estimated.'}
			</Typography>
			<Autocomplete multiple size="small"
			              options={library}
			              value={selected}
			              loading={loading}
			              disableCloseOnSelect
			              groupBy={(t) => DIM_LABEL[t.dimension] || t.dimension}
			              getOptionLabel={(t) => t.name}
			              // A category tether cannot be picked (criterion 3); one already chosen stays, with its note.
			              getOptionDisabled={(t) => t.dimension === 'category' && !selectedIds.includes(t.id)}
			              isOptionEqualToValue={(a, b) => a.id === b.id}
			              onChange={(_, next) => onChange(next)}
			              renderTags={(list, getTagProps) =>
			                list.map((t, idx) => (
			                  <TetherTag {...getTagProps({ index: idx })}
			                             key={t.id} tether={t}/>
			                ))}
			              renderOption={(props, t) => (
			                <li {...props} key={t.id}
			                    style={{ ...props.style, alignItems: 'flex-start',
			                             paddingTop: 6, paddingBottom: 6 }}>
			                  <TetherOptionRow tether={t} check={checks.check(t)}/>
			                </li>
			              )}
			              renderInput={(params) => (
			                <TextField {...params}
			                           placeholder={loading ? 'Loading tethers…' : selected.length ? '' : 'Pick one or more tethers…'}/>
			              )}/>

			{selected.length > 0 && (
				<Stack gap={0.5}>
					{selected.map(t => (
						<TetherPreviewRow key={t.id} tether={t} check={checks.check(t)}/>
					))}
				</Stack>
			)}
		</Stack>
	)
}

function TetherTag({ tether, ...rest }) {
	const p = DIM_PALETTE[tether.dimension] || {}
	return (
		<Chip {...rest} size="small"
		      label={tether.name}
		      sx={{ height: 22, fontSize: 11, fontWeight: 700,
		            bgcolor: p.bg, color: p.fg,
		            border: '1px solid', borderColor: p.border }}/>
	)
}

/** Amber when the tether does nothing or only part of it here; nothing when it applies in full. */
const warned = (check) => !!check?.sentence

function TetherOptionRow({ tether, check }) {
	const shortLabel = useValueLabel()
	const p = DIM_PALETTE[tether.dimension] || {}
	const children = tether.children || (tether.child ? [tether.child] : [])
	return (
		<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
			<Stack direction="row" gap={0.75} alignItems="center">
				<Chip size="small" label={DIM_LABEL[tether.dimension] || tether.dimension}
				      sx={{ height: 18, fontSize: 11, fontWeight: 700,
				            bgcolor: p.bg, color: p.fg,
				            border: '1px solid', borderColor: p.border,
				            letterSpacing: '0.04em' }}/>
				<Typography sx={{ fontSize: 13, fontWeight: 700 }}>
					{tether.name}
				</Typography>
			</Stack>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
				{shortLabel(tether.dimension, tether.parent)}{' '}→{' '}
				{children.map(c => shortLabel(tether.dimension, c)).join(', ') || '…'}
				{'  ·  '}<Box component="span" sx={{ fontWeight: 700,
				                                     color: (tether.delta?.v ?? 0) >= 0 ? dsColorNew.successText : dsColorNew.dangerRed }}>
					{formatDelta(tether.delta)}
				</Box>
			</Typography>
			{warned(check) && (
				<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber }}>{check.sentence}</Typography>
			)}
		</Box>
	)
}

function TetherPreviewRow({ tether, check }) {
	const shortLabel = useValueLabel()
	const p = DIM_PALETTE[tether.dimension] || {}
	const children = tether.children || (tether.child ? [tether.child] : [])
	const amber = warned(check)
	const chipSx = {
		height: 22, fontSize: 12, fontWeight: 700,
		bgcolor: dsColorNew.white,
		border: '1px solid', borderColor: p.border,
		color: p.fg,
		'& .MuiChip-label': { px: 0.75 },
	}
	// A child outside this strategy is struck through: it is not moved here (criterion 10).
	const struckSx = { ...chipSx, color: dsColorNew.textSubtle, borderStyle: 'dashed',
	                   '& .MuiChip-label': { px: 0.75, textDecoration: 'line-through' } }
	return (
		<Box sx={{ px: 1, py: 0.5,
		           bgcolor: amber ? dsColorNew.noteAmberBg : dsColorNew.tableRowAlt,
		           border: `1px solid ${amber ? dsColorNew.noteAmberBorder : dsColorNew.grayBorder}`,
		           borderRadius: '8px' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
				<Chip size="small" label={shortLabel(tether.dimension, tether.parent)} sx={chipSx}/>
				<Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
					<Box sx={{ height: 1, bgcolor: dsColorNew.grayBorder, width: 6 }}/>
					<Box sx={{ fontSize: 11, fontWeight: 700,
					           bgcolor: dsColorNew.white,
					           border: '1px solid', borderColor: dsColorNew.grayBorder,
					           borderRadius: 0.5, px: 0.5, py: 0.1,
					           color: (tether.delta?.v ?? 0) > 0 ? dsColorNew.successText
					                : (tether.delta?.v ?? 0) < 0 ? dsColorNew.dangerRed : dsColorNew.textMuted }}>
						{formatDelta(tether.delta)}
					</Box>
					<Box sx={{ height: 1, bgcolor: dsColorNew.grayBorder, width: 6 }}/>
					<ArrowIcon sx={{ fontSize: 18, color: dsColorNew.textSubtle }}/>
				</Box>
				<Stack direction="row" gap={0.35} flexWrap="wrap">
					{children.length === 0
					  ? <Chip size="small" label="—"
					          sx={{ ...chipSx, borderStyle: 'dashed', color: dsColorNew.textSubtle }}/>
					  : children.map(c => (
					      <Chip key={c} size="small"
					            label={shortLabel(tether.dimension, c)}
					            sx={check?.misfits?.includes(String(c)) ? struckSx : chipSx}/>
					    ))}
				</Stack>
			</Box>
			{amber && (
				<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber, mt: 0.5 }}>
					{check.sentence}. You can still save.
				</Typography>
			)}
		</Box>
	)
}
